import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Users, Plus, Compass, Search } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const KINDS = ["all", "official", "community"];

export default function Clubs() {
    const { t, i18n } = useTranslation();
    const [items, setItems] = useState(null);
    const [kind, setKind] = useState("all");
    const [q, setQ] = useState("");
    // Default to user's UI language so a Spanish reader doesn't see English
    // clubs whose books would be picked in English.
    const userLang = (i18n.language || "en").split("-")[0];
    const [language, setLanguage] = useState(userLang);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const params = {};
                if (kind !== "all") params.kind = kind;
                if (q.trim()) params.q = q.trim();
                if (language) params.language = language;
                const { data } = await api.get("/clubs", { params });
                if (alive) setItems(data);
            } catch (e) {
                toast.error(formatApiError(e.response?.data?.detail));
                if (alive) setItems([]);
            }
        })();
        return () => { alive = false; };
    }, [kind, q, language]);

    return (
        <main className="min-h-[calc(100vh-73px)] max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-16 md:py-24">
            <div className="border-b border-[var(--border)] pb-10 mb-12">
                <p className="text-xs uppercase tracking-[0.3em] text-[var(--accent)] mb-3">
                    {t("clubs.eyebrow")}
                </p>
                <div className="flex flex-wrap items-end justify-between gap-4">
                    <h1
                        className="font-display text-5xl md:text-7xl text-[var(--ink)] tracking-tight"
                        style={{ fontVariationSettings: '"wght" 700, "wdth" 75, "opsz" 144' }}
                    >
                        {t("clubs.title")}
                    </h1>
                    <div className="flex items-center gap-3">
                        <Link
                            to="/clubs/me"
                            data-testid="clubs-my-cta"
                            className="border border-[var(--ink)] text-[var(--ink)] rounded-full hover:bg-[var(--bg-hover)] transition-colors px-5 py-2 text-sm uppercase tracking-widest"
                        >
                            {t("clubs.my_clubs")}
                        </Link>
                        <Link
                            to="/clubs/new"
                            data-testid="clubs-create-cta"
                            className="inline-flex items-center gap-2 bg-[var(--ink)] text-[var(--bg)] rounded-full hover:bg-[var(--accent)] transition-colors px-5 py-2 text-sm uppercase tracking-widest"
                        >
                            <Plus size={14} strokeWidth={1.8} />
                            {t("clubs.create_cta")}
                        </Link>
                    </div>
                </div>
                <p className="text-[var(--ink-muted)] mt-4 text-lg max-w-2xl">{t("clubs.lead")}</p>
            </div>

            <div className="flex flex-wrap items-center gap-4 mb-10">
                <div className="flex items-center gap-2 border border-[var(--border-strong)] rounded-full px-4 py-1.5 max-w-md flex-1 min-w-[200px]">
                    <Search size={14} strokeWidth={1.5} className="text-[var(--ink-dim)]" />
                    <input
                        data-testid="clubs-search"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder={t("clubs.search_placeholder")}
                        className="flex-1 bg-transparent border-0 outline-none text-sm text-[var(--ink)] py-1"
                    />
                </div>
                <div className="flex items-center gap-1 text-xs uppercase tracking-widest text-[var(--ink-muted)]">
                    {KINDS.map((k) => (
                        <button
                            key={k}
                            data-testid={`clubs-filter-${k}`}
                            onClick={() => setKind(k)}
                            className={`px-3 py-1.5 rounded-full transition-colors ${
                                kind === k
                                    ? "bg-[var(--ink)] text-[var(--bg)]"
                                    : "border border-[var(--border-strong)] text-[var(--ink-muted)] hover:text-[var(--ink)]"
                            }`}
                        >
                            {t(`clubs.kind_${k}`)}
                        </button>
                    ))}
                </div>
                <select
                    data-testid="clubs-language-filter"
                    value={language}
                    onChange={(e) => setLanguage(e.target.value)}
                    className="bg-transparent border border-[var(--border-strong)] rounded-full px-4 py-1.5 text-xs uppercase tracking-widest text-[var(--ink-muted)]"
                >
                    <option value={userLang}>{t("clubs.this_language")}</option>
                    <option value="">{t("clubs.any_language")}</option>
                    <option value={userLang === "es" ? "en" : "es"}>
                        {userLang === "es" ? "English" : "Español"}
                    </option>
                </select>
            </div>

            {items === null ? (
                <p className="font-display text-2xl italic text-[var(--ink)] animate-slow-pulse">…</p>
            ) : items.length === 0 ? (
                <div className="text-center py-24 border border-[var(--border-strong)] bg-[var(--bg-elevated)]">
                    <Compass size={36} strokeWidth={1} className="mx-auto text-[var(--accent)] mb-4" />
                    <p className="font-display text-3xl text-[var(--ink)] mb-3"
                       style={{ fontVariationSettings: '"wght" 600, "wdth" 90, "opsz" 64' }}>
                        {t("clubs.empty_title")}
                    </p>
                    <p className="text-[var(--ink-muted)] mb-8 max-w-md mx-auto">{t("clubs.empty_lead")}</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                    {items.map((club, i) => (
                        <Link
                            key={club.id}
                            to={`/clubs/${club.slug}`}
                            data-testid={`club-card-${i}`}
                            className="block bg-[var(--bg-elevated)] border border-[var(--border)] hover:border-[var(--accent)] p-6 transition-all hover:-translate-y-1 group"
                        >
                            <div className="flex items-center justify-between mb-4">
                                <span className={`text-[10px] uppercase tracking-[0.25em] ${club.kind === "official" ? "text-[var(--accent)]" : "text-[var(--ink-dim)]"}`}>
                                    {t(`clubs.kind_${club.kind}`)} · {club.language?.toUpperCase()}
                                </span>
                                <span className="inline-flex items-center gap-1 text-xs text-[var(--ink-dim)]">
                                    <Users size={12} strokeWidth={1.5} />
                                    {club.member_count}
                                </span>
                            </div>
                            <h3 className="font-display text-2xl text-[var(--ink)] leading-tight mb-2 group-hover:text-[var(--accent)] transition-colors"
                                style={{ fontVariationSettings: '"wght" 600, "wdth" 88, "opsz" 32' }}>
                                {club.name}
                            </h3>
                            <p className="text-sm text-[var(--ink-muted)] line-clamp-3 mb-4">{club.theme}</p>
                            <p className="text-[10px] uppercase tracking-widest text-[var(--accent)]">
                                {t("clubs.cycles_count", { count: club.cycle_count || 0 })}
                            </p>
                        </Link>
                    ))}
                </div>
            )}
        </main>
    );
}
